import { FC, useEffect, useMemo, useState } from 'react';
import { StudentDetailsViewProps } from './studentDetails.interfaces';
import { Grid, Alert, Box } from '@mui/material';
import { pages } from 'src/utils/common';
import {
  getStudentDetailsTabMenuItems,
  getStudentDetailsTabIndex,
  getStudentDetailsSubRouteId,
  studentDetailsSubRouteIds,
} from './studentDetails.helpers';
import MainTitle from 'src/views/components/mainTitle';
import TabGroup from 'src/views/components/tabGroup';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from 'src/utils/CONSTS';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { PermissionGate } from 'src/components/rbac/PermissionGate';
import { RbacDeniedRedirect } from 'src/components/rbac/RbacDeniedRedirect';

const StudentDetailsView: FC<StudentDetailsViewProps> = () => {
  const { translateText } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { studentId, tab } = useParams<{ studentId: string; tab: string }>();
  const navigate = useNavigate();

  const tabMenuItems = useMemo(
    () => (studentId ? getStudentDetailsTabMenuItems(translateText, studentId) : []),
    [translateText, studentId]
  );

  const handleBackToStudentList = () => {
    navigate(pages.students.path);
  };

  const locationChangeHandler = (value: string | number) => {
    if (!studentId) return;

    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    const routeId = getStudentDetailsSubRouteId(tabIndex);
    navigate(`${pages.students.path}/${studentId}/details/${routeId}`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (!studentId) {
      return;
    }
    const currentTab = tab && tab !== 'undefined' ? tab : studentDetailsSubRouteIds[0];
    const tabIndex = getStudentDetailsTabIndex(currentTab);

    setSelectedTab(tabIndex);

    if (!tab || tab === 'undefined') {
      navigate(
        `${pages.students.path}/${studentId}/details/${studentDetailsSubRouteIds[0]}`,
        {
          replace: true,
        }
      );
    }
  }, [studentId, tab, navigate]);

  if (!studentId) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
        }}
        data-testid="student-details-container"
      >
        <Grid
          container
          sx={{
            height: '100%',
            px: { xs: 0, sm: 0 },
          }}
        >
          <Grid item xs={12} sx={{ p: { xs: 1, sm: 2 } }}>
            <Alert severity="error" data-testid="student-details-no-student-id">
              {translateText('students.studentDetails.errors.noStudentId')}
            </Alert>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        pb: '40px',
        boxSizing: 'border-box'
      }}
      data-testid="student-details-container"
    >
      <Helmet>
        <title>{CONSTANTS.title.studentDetails}</title>
      </Helmet>
      <PermissionGate
        module="students"
        submodule="studentList"
        permission="read"
        fallback={<RbacDeniedRedirect />}
      >
        <Grid
          container
          sx={{
            height: '100%',
            px: { xs: 0, sm: 0 },
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              pr: { xs: 0, sm: 4 },
            }}
            data-testid="student-details-header-container"
          >
            <MainTitle
              title={translateText('students.studentDetails.title')}
              subTitle={tabMenuItems[selectedTab]?.label}
              backButtonText={translateText('students.studentDetails.backToStudentList')}
              onBackButtonClickHandler={handleBackToStudentList}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              height: '100%',
              px: 6,
              boxSizing: 'border-box',
              '& *': {
                boxSizing: 'border-box',
              }
            }}
            data-testid="student-details-tabs-container"
          >
            <TabGroup
              key={selectedTab}
              tabs={tabMenuItems}
              initiallySelected={selectedTab}
              onTabChange={(value: number) => locationChangeHandler(value)}
              dataTestIdPrefix="student-details"
            />
          </Grid>
        </Grid>
      </PermissionGate>
    </Box >
  );
};

export default StudentDetailsView;
